/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import { useState } from 'react'
import classNames from 'classnames'
import ExpandableArrow from 'components/expandable-arrow'
import Intro from 'components/intro'
import Link from 'next/link'
import type { NextStepsProps } from './types'
import { StandaloneLink } from '@hashicorp/mds-react/components'
import useProductMeta from '@web/product-meta'
import s from './style.module.css'

export default function NextSteps({
	appearance = 'light',
	theme = 'hashicorp',
	heading,
	description,
	steps,
	cta,
}: NextStepsProps) {
	const { themeClass } = useProductMeta(theme)
	return (
		<section
			className={classNames(s.nextSteps, s[appearance], themeClass)}
			data-testid="next-steps"
		>
			<div className={s.container}>
				<div className={s.content}>
					<Intro
						appearance={appearance}
						heading={heading}
						description={description}
					/>
				</div>
				<div className={s.stepsWrapper}>
					<ul className={s.stepsList}>
						{steps.map((step, index) => {
							const isFeatured = steps.length === 3 && index === 0
							const variant = index === 0 ? 'primary' : 'secondary'
							return (
								<li
									// eslint-disable-next-line react/no-array-index-key
									key={index}
									className={classNames(
										s.stepsListItem,
										isFeatured && s.stepsListItemFeature,
									)}
								>
									<Tile
										variant={variant}
										cta={{
											title: step.cta.title,
											url: step.cta.url,
										}}
										heading={step.heading}
										description={step.description}
									/>
								</li>
							)
						})}
					</ul>
					{cta ? (
						<div className={s.cta}>
							<p className={s.ctaCopy}>{cta.copy}</p>
							<StandaloneLink
								href={cta.href}
								text={cta.ctaText}
								icon="arrow-right"
								iconPosition="trailing"
							/>
						</div>
					) : null}
				</div>
			</div>
		</section>
	)
}

function Tile({ variant, heading, description, cta }) {
	const [isHovered, setIsHovered] = useState(false)
	return (
		<Link href={cta.url} legacyBehavior>
			<a
				className={classNames(s.tile, s[variant])}
				onMouseOver={() => {
					setIsHovered(true)
				}}
				onMouseOut={() => {
					setIsHovered(false)
				}}
			>
				<div className={s.tileInner}>
					<h3 className={s.tileHeading}>
						<span className={s.tileHeadingText}>{heading}</span>
					</h3>
					{description ? (
						<p className={s.tileDescription}>{description}</p>
					) : null}
					<span className={s.tileCta}>
						<span className={s.tileCtaLabel}>{cta.title}</span>
						<span className={s.tileCtaIcon}>
							<ExpandableArrow expanded={isHovered} />
						</span>
					</span>
				</div>
				<span className={s.tileScrim} />
			</a>
		</Link>
	)
}
